'use strict';

/**
 * Pict-Provider-WorkflowAPI
 * -------------------------
 * The client the retold-workflow views talk to, pointed at plansheet's /1.0/Workflow routes. It
 * wraps retold-workflow's reference WorkflowClient for the workflow-native verbs (types, adopt,
 * board, layout, and the per-subject operations) and fills the two methods plansheet serves
 * through its existing generic entity CRUD rather than a dedicated workflow route:
 *
 *   getType(id)            -> the matching record from the catalog list (which already carries
 *                             WorkflowDefinition), so no extra route is needed.
 *   saveType(id, def)      -> PUT /1.0/VisionType with the new WorkflowDefinition (the generic,
 *                             tenant-scoped entity update meadow already exposes).
 *
 * The views resolve their client by provider name ('WorkflowAPI'); this provider is that object.
 */

const libPictProvider = require('pict').ServiceProviderBase;
const libWorkflowClient = require('retold-workflow').WorkflowClient;
const libFrameworkClient = require('./FrameworkClient.js');

const _DefaultProviderConfiguration =
{
	ProviderIdentifier: 'WorkflowAPI',
	Dependencies: [],
	BasePath: '/1.0/Workflow',
	TypeEntityRoute: '/1.0/VisionType'
};

class PictProviderWorkflowAPI extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		let tmpOptions = Object.assign({}, _DefaultProviderConfiguration, pOptions || {});
		super(pFable, tmpOptions, pServiceHash);
		this._client = new libWorkflowClient({ BasePath: this.options.BasePath, Credentials: 'same-origin' });
	}

	// -- workflow-native verbs (delegate to the reference client) ---------------
	getTypes() { return this._client.getTypes(); }
	// Owned-only types (the tenant's imported crafts, no platform catalog). The reference client
	// only knows the union route, so this hits plansheet's /Types/Owned directly and unwraps it.
	getOwnedTypes()
	{
		return libFrameworkClient.getJSON(this.fable, this.options.BasePath + '/Types/Owned')
			.then((pBody) => (pBody && pBody.Types) || []);
	}
	adoptType(pBuiltInID) { return this._client.adoptType(pBuiltInID); }
	getBoard(pTypeID) { return this._client.getBoard(pTypeID); }
	getLayout(pTypeID) { return this._client.getLayout(pTypeID); }
	saveLayout(pTypeID, pLayout) { return this._client.saveLayout(pTypeID, pLayout); }
	open(pSubjectID) { return this._client.open(pSubjectID); }
	advance(pSubjectID, pToState) { return this._client.advance(pSubjectID, pToState); }
	reevaluate(pSubjectID) { return this._client.reevaluate(pSubjectID); }
	getSubject(pSubjectID) { return this._client.getSubject(pSubjectID); }
	getTimeline(pSubjectID) { return this._client.getTimeline(pSubjectID); }
	getMetrics(pSubjectID) { return this._client.getMetrics(pSubjectID); }
	getAgency(pSubjectID) { return this._client.getAgency(pSubjectID); }

	// -- served through plansheet's generic VisionType CRUD ---------------------

	getType(pTypeID)
	{
		return this.getTypes().then((pTypes) => (pTypes || []).find((pType) => String(pType.ID) === String(pTypeID)) || null);
	}

	saveType(pTypeID, pDefinition)
	{
		let tmpBody = { IDVisionType: pTypeID, WorkflowDefinition: JSON.stringify(pDefinition || {}) };
		return libFrameworkClient.putJSON(this.fable, this.options.TypeEntityRoute, tmpBody);
	}

	// -- Drafting Kit library (plansheet /1.0/Workflow/Types routes) -------------

	// One JSON request against BasePath; throws on a non-2xx with the server's Error message.
	_req(pPath, pMethod, pBody)
	{
		let tmpURL = this.options.BasePath + pPath;
		let tmpMethod = (pMethod || 'GET').toUpperCase();
		switch (tmpMethod)
		{
			case 'POST':
				return libFrameworkClient.postJSON(this.fable, tmpURL, pBody);
			case 'PUT':
				return libFrameworkClient.putJSON(this.fable, tmpURL, pBody);
			case 'DELETE':
				return libFrameworkClient.delJSON(this.fable, tmpURL);
			default:
				return libFrameworkClient.getJSON(this.fable, tmpURL);
		}
	}

	getLibrary() { return this._req('/Types/Library'); }
	getKit(pID) { return this._req('/Types/' + pID + '/Kit').then((pBody) => (pBody && pBody.Kit) || null); }
	createKit(pName, pFromTemplateID) { return this._req('/Types', 'POST', { Name: pName, FromTemplateID: pFromTemplateID || 0 }).then((pBody) => (pBody && pBody.Kit) || null); }
	duplicateKit(pID) { return this._req('/Types/' + pID + '/Duplicate', 'POST', {}).then((pBody) => (pBody && pBody.Kit) || null); }
	renameKit(pID, pName) { return this._req('/Types/' + pID + '/Name', 'PUT', { Name: pName }); }
	deleteKit(pID) { return this._req('/Types/' + pID, 'DELETE'); }
	saveKitTerms(pID, pTerms) { return this._req('/Types/' + pID + '/Terms', 'PUT', { Terms: pTerms }); }
	saveKitConnectionStyle(pID, pStyle) { return this._req('/Types/' + pID + '/ConnectionStyle', 'PUT', { ConnectionStyle: pStyle }); }
	saveKitDefaultStickerSet(pID, pKey) { return this._req('/Types/' + pID + '/DefaultStickerSet', 'PUT', { DefaultStickerSet: pKey || '' }); }
	saveKitBrandStickerSet(pID, pKey) { return this._req('/Types/' + pID + '/BrandStickerSet', 'PUT', { BrandStickerSet: pKey || '' }); }
	saveKitLoops(pID, pLoops) { return this._req('/Types/' + pID + '/Loops', 'PUT', { Loops: pLoops }); }
	saveKitLoopLabels(pID, pLabels) { return this._req('/Types/' + pID + '/LoopLabels', 'PUT', { LoopLabels: pLabels }); }
}

module.exports = PictProviderWorkflowAPI;
module.exports.default_configuration = _DefaultProviderConfiguration;
